// src/pages/Publisher/Support/Support.tsx
import React, { useEffect } from 'react';
import FilterSelect from '@/components/filters/FilterSelect';
import Search from '@/components/filters/Search';
import AddNewGamicsButton from '@/components/buttons/AddNewGamicsButton';
import SupportTable from './components/SupportTable';
import AddSupportTicket from './AddSupportTicket';
import { Status, SupportTicket } from './types';
import { fetchMyTickets } from '@/store/Ticket';
import { useDispatch } from 'react-redux';

const statusList: (Status | 'All')[] = ['Pending', 'On Going', 'Resolved'];

const Support: React.FC = () => {
  const [status, setStatus] = React.useState<Status | 'All'>('All');
  const [search, setSearch] = React.useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = React.useState<string>('');

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [rowIdx, setRowIdx] = React.useState<string | null>(null);
  const [tickets, setTickets] = React.useState<SupportTicket[]>([]);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const dispatch = useDispatch<any>();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 420);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    const data = async () => {
      const searchParam = debouncedSearch || undefined;
      const params = {
        status:
          status === 'All'
            ? undefined
            : status === 'On Going'
              ? 'in_progress'
              : status.toLowerCase(),
        search: searchParam,
      };

      const res = await dispatch(fetchMyTickets({ ...params }));
      setTickets(res.payload.data);
    };
    data();
  }, [status, debouncedSearch]);

  return (
    <div className='p-8 rounded-[24px] bg-white flex flex-col h-full min-h-0 gap-y-[56px]'>
      {/* filters + search + add-button */}
      <div className='flex flex-wrap items-center gap-4'>
        <FilterSelect
          value={status}
          onChange={(e) => setStatus(e.target.value as Status | 'All')}
          options={[
            { value: 'All', label: 'Status: All' },
            { value: 'Pending', label: 'Status: Pending' },
            { value: 'Resolved', label: 'Status: Resolved' },
            { value: 'On Going', label: 'Status: On Going' },
          ]}
        />

        <Search
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Search Issue and Description'
        />

        <div className='ml-auto w-[226px] inline-flex items-center'>
          <AddNewGamicsButton
            label='Add New Ticket'
            responsive={false}
            onClick={() => setIsModalOpen(true)}
          />
        </div>
      </div>

      {/* table */}
      <div className='flex-1 min-h-0'>
        <SupportTable
          data={tickets}
          anchorEl={anchorEl}
          rowIdx={rowIdx}
          onMenuOpen={(e, idx) => {
            setAnchorEl(e.currentTarget);
            setRowIdx(idx);
          }}
          onMenuClose={() => {
            setAnchorEl(null);
            setRowIdx(null);
          }}
          onDelete={(idx) => {
            setTickets((curr) => curr.filter((t) => t.id !== idx));
            setAnchorEl(null);
            setRowIdx(null);
          }}
          onArchive={(idx) => {
            // TODO: archive logic
            setAnchorEl(null);
            setRowIdx(null);
          }}
        />
      </div>

      {isModalOpen && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-6'
          role='dialog'
          aria-modal='true'
          aria-label='New support ticket form'
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className='relative w-full max-w-3xl'
            onClick={(event) => event.stopPropagation()}
          >

            <AddSupportTicket onClose={() => setIsModalOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;
